import PropTypes from "prop-types";
import { useRef, useState, useEffect, useCallback } from "react";

import Check from "@mui/icons-material/Check";
import OpenInNew from "@mui/icons-material/OpenInNew";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import {
  Box,
  Chip,
  Alert,
  Stack,
  Button,
  Dialog,
  Tooltip,
  TextField,
  Accordion,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";

import { currentFiles, requestStatus, requestSummary } from "./request-status";

const labels = {
  unreviewed: "Senza esito",
  pending: "Da verificare",
  accepted: "Accettato",
  rejected: "Respinto",
};
const fileMeta = (file) =>
  [
    file.name?.match(/\.([^.]+)$/)?.[1]?.toUpperCase(),
    file.size > 0 &&
      (file.size < 1024 * 1024
        ? `${Math.max(1, Math.round(file.size / 1024))} KB`
        : `${(file.size / 1024 / 1024).toLocaleString("it-IT", { maximumFractionDigits: 1 })} MB`),
    file.uploaded_at &&
      `caricato il ${new Date(file.uploaded_at).toLocaleDateString("it-IT")}`,
  ]
    .filter(Boolean)
    .join(" · ");
const colors = {
  pending: "warning",
  accepted: "success",
  rejected: "error",
  unreviewed: "default",
};
const requestColors = {
  "Da caricare": "default",
  "In verifica": "warning",
  "Da ricaricare": "error",
  Accettato: "success",
  Completata: "success",
  Annullata: "default",
};
// Cartella leggibile: "AZIENDA/Contratti/" diventa "AZIENDA › Contratti".
export const folderName = (folder = "") =>
  folder.replace(/\/$/, "").split("/").join(" › ");
// Spiega allo studio quando può completare una richiesta.
const completionHint = (current) => {
  if (!current.length)
    return "Potrai completarla quando l’azienda avrà caricato il documento.";
  if (current.some((file) => file.review_status !== "accepted"))
    return "Accetta tutti i documenti ricevuti per completare la richiesta.";
  return "Tutti i documenti sono accettati: puoi completare la richiesta.";
};
const message = (error) => {
  const value = error?.response?.data?.message;
  return (
    (Array.isArray(value) ? value.join(". ") : value) ||
    (error?.response
      ? "Operazione non riuscita. Riprova tra qualche istante."
      : "Il server non risponde. Controlla la connessione di rete e riprova.")
  );
};
const freshId = () => crypto.randomUUID();

export default function DocumentWorkflow({
  companyId,
  service,
  studio = false,
  scope = "documents",
  path = "",
  onChanged,
  onOpenFile,
  onSummaryChange,
}) {
  const [data, setData] = useState(null);
  // La verifica dei file senza richiesta resta solo nell'onboarding: nell'archivio
  // si verificano soltanto i documenti richiesti dallo studio.
  const compactArchive = studio && scope === "onboarding";
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestDraft, setRequestDraft] = useState(null);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [retryUploads, setRetryUploads] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const sequence = useRef(0);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    sequence.current += 1;
    const serial = sequence.current;
    try {
      const next = await service.list(companyId, scope, path);
      if (mounted.current && serial === sequence.current) {
        setData(next);
        setError("");
      }
    } catch (failure) {
      if (mounted.current && serial === sequence.current)
        setError(message(failure));
    }
  }, [companyId, scope, path, service]);
  useEffect(() => {
    mounted.current = true;
    setData(null);
    load();
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, [load]);
  const run = async (work, dialog = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setDialogError("");
    try {
      await work();
      await load();
      onChanged?.();
    } catch (failure) {
      if (mounted.current)
        (dialog ? setDialogError : setError)(message(failure));
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const uploadJobs = async (jobs) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const failed = [];
    await jobs.reduce(async (previous, job) => {
      await previous;
      try {
        await service.upload(companyId, scope, job.input, job.file);
      } catch (failure) {
        failed.push({ ...job, error: message(failure) });
      }
    }, Promise.resolve());
    if (mounted.current) {
      setRetryUploads(failed);
      setBusy(false);
    }
    inFlight.current = false;
    await load();
    onChanged?.();
  };
  const choose = (event, request, previous) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (inFlight.current || !files.length) return;
    if (files.some((file) => !file.size || file.size > 20 * 1024 * 1024)) {
      setError("Carica file non vuoti, entro 20 MB ciascuno.");
      return;
    }
    const folder =
      request?.folder ||
      path ||
      (scope === "onboarding" ? "AZIENDA/Onboarding/" : "AZIENDA/");
    uploadJobs(
      files.map((file) => ({
        file,
        input: {
          id: freshId(),
          folder,
          ...(request && { requestId: request.id }),
          ...(previous && { replacesId: previous.id }),
        },
      })),
    );
  };
  const uploadButton = (
    request,
    previous,
    label = "Carica documenti",
    compact = false,
  ) => (
    <Button
      component="label"
      size="small"
      variant="outlined"
      startIcon={compact ? <UploadFileOutlined /> : undefined}
      sx={
        compact
          ? { flexShrink: 0, alignSelf: "flex-start", whiteSpace: "nowrap" }
          : undefined
      }
      disabled={busy || Boolean(retryUploads.length)}
    >
      {label}
      <input
        hidden
        type="file"
        multiple={!previous}
        aria-label={
          previous
            ? `Nuova versione di ${previous.name}`
            : `Carica ${request?.title || "documenti aziendali"}`
        }
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
        disabled={busy || Boolean(retryUploads.length)}
        onChange={(event) => choose(event, request, previous)}
      />
    </Button>
  );
  const requestBaseFolder =
    scope === "onboarding" ? "AZIENDA/Onboarding/" : "AZIENDA/";
  const defaultFolder =
    path && ["AZIENDA", "COMUNICA ALLO STUDIO"].includes(path.split("/")[0])
      ? path
      : requestBaseFolder;
  const allFiles = data?.files || [];
  const summary =
    data && scope === "onboarding"
      ? requestSummary(data.requests, allFiles)
      : null;
  const summaryKey = summary ? `${summary.accepted}/${summary.total}` : "";
  useEffect(() => {
    if (summary) onSummaryChange?.(summary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey, onSummaryChange]);
  const documentsView = scope === "documents";
  const folderPath = path && !path.endsWith("/") ? `${path}/` : path;
  // L'azienda vede solo le richieste da gestire; lo studio le vede tutte.
  // Nei Documenti compaiono solo le richieste aperte (i file restano nelle
  // cartelle) e, dentro una cartella, solo le sue.
  const visibleRequests = (data?.requests || []).filter(
    (request) =>
      (studio ||
        (request.status !== "cancelled" &&
          (scope === "onboarding" || request.status !== "completed"))) &&
      (!documentsView ||
        (request.status === "open" &&
          (!folderPath || request.folder?.startsWith(folderPath)))),
  );
  const isPrevious = (file) =>
    allFiles.some((next) => next.replaces_id === file.id);
  const row = (file, request, historical = false, compact = false) => (
    <Stack
      key={file.id || file.key}
      spacing={0.75}
      sx={{ py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0, ...(compact && { flex: 1 }) }}>
          {compact ? (
            <Tooltip title={file.name} describeChild>
              <Stack
                direction="row"
                spacing={0}
                alignItems="center"
                tabIndex={0}
                sx={{ minWidth: 0 }}
              >
                <DescriptionOutlined
                  fontSize="small"
                  sx={{ color: "text.secondary", flexShrink: 0, mr: 1 }}
                />
                <Typography
                  variant="body2"
                  component="span"
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {file.name
                    .replace(
                      /[_-][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.[^.]+$|$)/i,
                      "",
                    )
                    .replace(/\.[^.]+$/, "")}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ flexShrink: 0, "&&": { ml: 0 } }}
                >
                  {file.name.match(/\.[^.]+$/)?.[0] || ""}
                </Typography>
              </Stack>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {file.name}
            </Typography>
          )}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Chip
              size="small"
              variant={compact ? "soft" : "outlined"}
              color={
                compact && file.review_status === "unreviewed"
                  ? colors.pending
                  : colors[file.review_status]
              }
              label={
                compact && file.review_status === "unreviewed"
                  ? labels.pending
                  : labels[file.review_status]
              }
            />
            {compact && (
              <Typography variant="caption" color="text.secondary">
                {fileMeta(file)}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={compact ? 1 : 0.5}
          sx={compact ? { flexShrink: 0 } : undefined}
        >
          <Button
            variant={compact ? "outlined" : "text"}
            color={compact ? "inherit" : "primary"}
            startIcon={compact ? <OpenInNew /> : undefined}
            disabled={busy}
            size="small"
            onClick={() =>
              run(async () => {
                if (compact && onOpenFile) {
                  await onOpenFile(file);
                  return;
                }
                const { url } = await service.download(companyId, scope, file);
                window.open(url, "_blank", "noopener,noreferrer");
              })
            }
          >
            Apri
          </Button>
          {studio &&
            !historical &&
            ["pending", "unreviewed"].includes(file.review_status) && (
              <>
                <Button
                  variant={compact ? "soft" : "text"}
                  color={compact ? "success" : "primary"}
                  startIcon={compact ? <Check /> : undefined}
                  disabled={busy}
                  size="small"
                  onClick={() =>
                    run(async () => {
                      const registered = file.id
                        ? file
                        : await service.register(companyId, scope, file);
                      await service.review(companyId, registered.id, scope, {
                        version: registered.version,
                        status: "accepted",
                      });
                    })
                  }
                >
                  Accetta
                </Button>
                <Button
                  disabled={busy}
                  size="small"
                  color="error"
                  onClick={() => {
                    setDecision(file);
                    setReason("");
                    setDialogError("");
                  }}
                >
                  Respingi
                </Button>
              </>
            )}
          {!historical &&
            !studio &&
            file.review_status === "rejected" &&
            (!request || request.status === "open") &&
            uploadButton(request, file, "Carica nuova versione")}
        </Stack>
      </Stack>
      {file.review_reason && (
        <Typography variant="body2" color="error.main">
          {file.review_reason}
        </Typography>
      )}
      {file.reviewed_at && (
        <Typography variant="caption" color="text.secondary">
          Verificato il {new Date(file.reviewed_at).toLocaleDateString("it-IT")}
          {studio && file.reviewed_by ? ` da ${file.reviewed_by}` : ""}
        </Typography>
      )}
    </Stack>
  );
  const history = (files, request) => {
    const older = files.filter(isPrevious);
    return (
      older.length > 0 && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <AccordionSummary>
            <Typography variant="body2">Storico ({older.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {older.map((file) => row(file, request, true))}
          </AccordionDetails>
        </Accordion>
      )
    );
  };
  const spontaneous = allFiles.filter(
    (file) =>
      !file.request_id &&
      (!path || file.key?.split("/DOCUMENTS/")[1]?.startsWith(path)),
  );
  const requestCard = (request) => {
    const files = allFiles.filter((file) => file.request_id === request.id);
    const current = currentFiles(files);
    const statusLabel = requestStatus(request, current);
    return (
      <Box
        key={request.id}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Typography variant="subtitle2">{request.title}</Typography>
            <Chip
              size="small"
              variant="outlined"
              color={requestColors[statusLabel]}
              label={statusLabel}
            />
          </Stack>
          {request.instructions && (
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            >
              {request.instructions}
            </Typography>
          )}
          {current.map((file) => row(file, request))}
          {history(files, request)}
          {request.status === "open" && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!studio && uploadButton(request)}
              {studio && (
                <>
                  <Button
                    size="small"
                    disabled={
                      busy ||
                      !current.length ||
                      current.some((file) => file.review_status !== "accepted")
                    }
                    onClick={() =>
                      run(() =>
                        service.closeRequest(companyId, request.id, scope, {
                          version: request.version,
                          status: "completed",
                        }),
                      )
                    }
                  >
                    Completa richiesta
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        service.closeRequest(companyId, request.id, scope, {
                          version: request.version,
                          status: "cancelled",
                        }),
                      )
                    }
                  >
                    Annulla richiesta
                  </Button>
                </>
              )}
            </Stack>
          )}
          {studio && request.status === "open" && (
            <Typography variant="caption" color="text.secondary">
              {completionHint(current)}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };
  const openRequests = visibleRequests.filter(
    (request) => request.status === "open",
  );
  // Da gestire: per lo studio i file da verificare, per l'azienda i file da caricare.
  const toHandle = openRequests.filter((request) => {
    const label = requestStatus(
      request,
      currentFiles(allFiles.filter((file) => file.request_id === request.id)),
    );
    return studio
      ? label === "In verifica"
      : ["Da caricare", "Da ricaricare"].includes(label);
  }).length;
  const newRequest = () => {
    setRequestDraft({
      id: freshId(),
      title: "",
      instructions: "",
      folder: defaultFolder,
    });
    setDialogError("");
  };
  // Le richieste vivono solo nelle cartelle in cui l'azienda può caricare.
  const requestFolder =
    !folderPath ||
    ["AZIENDA", "COMUNICA ALLO STUDIO"].includes(folderPath.split("/")[0]);
  const requestsPanel = data && requestFolder && (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded ?? toHandle > 0}
      onChange={(event, open) => setExpanded(open)}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded />}
        sx={{ "& .MuiAccordionSummary-content": { alignItems: "center" } }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Typography variant="subtitle1">
            {studio ? "Richieste all’azienda" : "Richieste dello studio"}
          </Typography>
          {toHandle > 0 ? (
            <Chip
              size="small"
              color="warning"
              label={`${toHandle} ${studio ? "da verificare" : "da caricare"}`}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {openRequests.length
                ? `${openRequests.length} in corso`
                : "Nessuna richiesta in corso"}
            </Typography>
          )}
        </Stack>
        {studio && (
          <Button
            variant="contained"
            size="small"
            disabled={busy}
            sx={{ mr: 1, flexShrink: 0 }}
            onFocus={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              newRequest();
            }}
          >
            Richiedi documento
          </Button>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {!openRequests.length && (
            <Typography variant="body2" color="text.secondary">
              {studio
                ? "Crea una richiesta per indicare all’azienda quali documenti fornire."
                : "Quando lo studio ti chiederà un documento, lo troverai qui con le istruzioni per caricarlo."}
            </Typography>
          )}
          {openRequests.map(requestCard)}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
  return (
    <Stack spacing={2} sx={{ mb: compactArchive ? 0 : 3 }}>
      {!documentsView && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          {compactArchive ? (
            <Stack spacing={0.5}>
              <Typography variant="h6">Richieste all’azienda</Typography>
              <Typography variant="body2" color="text.secondary">
                Chiedi all’azienda i documenti mancanti: qui vedrai lo stato di
                ogni richiesta.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="h6">Richieste dello studio</Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              disabled={busy}
              size="small"
              color={compactArchive ? "inherit" : "primary"}
              variant={compactArchive ? "outlined" : "text"}
              startIcon={
                compactArchive &&
                (busy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshRounded />
                ))
              }
              onClick={load}
            >
              Aggiorna
            </Button>
            {studio && (
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                onClick={newRequest}
              >
                {compactArchive ? "Nuova richiesta" : "Richiedi documento"}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={load}>
              Riprova
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {!data && !error && <CircularProgress size={24} />}
      {summary?.total > 0 && (
        <Typography variant="body2" color="text.secondary">
          {`${summary.accepted} di ${summary.total} documenti accettati`}
        </Typography>
      )}
      {data && !visibleRequests.length && !compactArchive && !documentsView && (
        <Typography variant="body2" color="text.secondary">
          {studio
            ? "Crea una richiesta per indicare all’azienda quali documenti fornire."
            : "Quando lo studio ti chiederà un documento, lo troverai qui con le istruzioni per caricarlo."}
        </Typography>
      )}
      {documentsView ? requestsPanel : visibleRequests.map(requestCard)}
      {retryUploads.map((job) => (
        <Alert key={job.input.id} severity="error">
          {job.file.name}: {job.error}
        </Alert>
      ))}
      {retryUploads.length > 0 && (
        <Stack direction="row" spacing={1}>
          <Button disabled={busy} onClick={() => uploadJobs(retryUploads)}>
            Riprova caricamento
          </Button>
          <Button
            disabled={busy}
            color="inherit"
            onClick={() => setRetryUploads([])}
          >
            Scegli altri file
          </Button>
        </Stack>
      )}
      {data && compactArchive && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Typography variant="subtitle2">
              Caricati senza richiesta (
              {spontaneous.filter((file) => !isPrevious(file)).length +
                data.existing.length}
              )
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Verifica i documenti caricati senza una richiesta dello
                  studio.
                </Typography>
                {uploadButton(null, null, "Carica file", true)}
              </Stack>
              {spontaneous
                .filter((file) => !isPrevious(file))
                .map((file) => row(file, undefined, false, true))}
              {history(spontaneous)}
              {data.existing.map((file) => row(file, undefined, false, true))}
              <Typography variant="caption" color="text.secondary">
                PDF, immagini, Word, Excel, CSV, TXT e ZIP · massimo 20 MB per
                file
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      <Dialog
        open={Boolean(requestDraft)}
        onClose={() => !busy && setRequestDraft(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Richiedi documento</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              required
              label="Documento da richiedere"
              value={requestDraft?.title || ""}
              onChange={(event) =>
                setRequestDraft({ ...requestDraft, title: event.target.value })
              }
              inputProps={{ maxLength: 255 }}
              disabled={busy}
            />
            <TextField
              label="Istruzioni (facoltative)"
              multiline
              minRows={2}
              value={requestDraft?.instructions || ""}
              onChange={(event) =>
                setRequestDraft({
                  ...requestDraft,
                  instructions: event.target.value,
                })
              }
              inputProps={{ maxLength: 4000 }}
              disabled={busy}
            />
            <Typography variant="caption" color="text.secondary">
              I documenti saranno salvati nella cartella{" "}
              {folderName(requestDraft?.folder)}
            </Typography>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRequestDraft(null)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            disabled={busy || !requestDraft?.title.trim()}
            onClick={() =>
              run(async () => {
                await service.createRequest(companyId, {
                  ...requestDraft,
                  origin: scope,
                });
                setRequestDraft(null);
              }, true)
            }
          >
            Crea richiesta
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(decision)}
        onClose={() => !busy && setDecision(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Respingi documento</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">{decision?.name}</Typography>
            <TextField
              autoFocus
              required
              label="Motivo del rifiuto"
              multiline
              minRows={3}
              value={reason}
              inputProps={{ maxLength: 4000 }}
              onChange={(event) => setReason(event.target.value)}
              disabled={busy}
            />
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDecision(null)}>
            Annulla
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || !reason.trim()}
            onClick={() =>
              run(async () => {
                const file = decision.id
                  ? decision
                  : await service.register(companyId, scope, decision);
                await service.review(companyId, file.id, scope, {
                  version: file.version,
                  status: "rejected",
                  reason,
                });
                setDecision(null);
              }, true)
            }
          >
            Respingi
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
DocumentWorkflow.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  service: PropTypes.object.isRequired,
  studio: PropTypes.bool,
  scope: PropTypes.oneOf(["documents", "onboarding"]),
  path: PropTypes.string,
  onChanged: PropTypes.func,
  onOpenFile: PropTypes.func,
  onSummaryChange: PropTypes.func,
};
